import { useState } from "react";
import { ToolContainer } from "@/components/ToolLayout";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useLogToolUsage } from "@/hooks/useLogToolUsage";
import { decodeJwt, isAlgNone, claimStatus } from "./jwtUtils";
import { NoticeBox } from "@/components/NoticeBox";

const CLAIMS: [string, string][] = [["iat", "Issued at"], ["nbf", "Not before"], ["exp", "Expires"]];

function claimDates(payload: unknown) {
  if (!payload || typeof payload !== "object") return [];
  const p = payload as Record<string, unknown>;
  return CLAIMS.flatMap(([k, label]) =>
    typeof p[k] === "number" ? [{ k, label, date: new Date((p[k] as number) * 1000) }] : []
  );
}

const TOOL_INFO_CONTENT = {
  what: `Decodes a JSON Web Token (JWT) and displays its header and payload contents in readable form, useful for debugging authentication issues.`,
  how: `Paste your JWT, and the decoded header and payload are displayed automatically. A leading "Bearer " prefix and any line breaks are ignored. Time claims are shown in UTC and in your local time.`,
  tip: `Decoding a JWT here reveals its contents but doesn't verify its cryptographic signature — a token can be decoded and read without confirming it's actually valid or unaltered.`,
};

export default function JwtDecoder() {
  const [token, setToken] = useState("");

  const decoded = decodeJwt(token);
  const result = decoded ? (decoded.ok ? decoded : { error: decoded.error }) : null;
  const algNone = !!decoded && decoded.ok && isAlgNone(decoded.alg);
  const now = Date.now();

  const { blocked } = useLogToolUsage(result);


  return (
    <ToolContainer>
      <div className="space-y-4">
        <div><Label htmlFor="jwt-input">JWT Token</Label><Textarea id="jwt-input" value={token} onChange={e => setToken(e.target.value)} placeholder="Paste your JWT here (a leading Bearer prefix is removed automatically)..." className="font-mono text-sm min-h-[100px]" /></div>
        {!blocked && result && "error" in result && <p role="alert" className="text-sm text-destructive">{result.error}</p>}
        {!blocked && algNone && <NoticeBox className="text-sm" role="alert">Warning: this token uses alg "none", meaning it is unsigned. Anyone can create or alter such a token, so a server must never accept it as proof of identity.</NoticeBox>}
        {!blocked && result && "header" in result && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 rounded-lg border bg-card"><Label className="text-xs text-muted-foreground">Header</Label><pre className="text-sm font-mono mt-2 whitespace-pre-wrap">{JSON.stringify(result.header, null, 2)}</pre></div>
            <div className="p-4 rounded-lg border bg-card"><Label className="text-xs text-muted-foreground">Payload</Label><pre className="text-sm font-mono mt-2 whitespace-pre-wrap">{JSON.stringify(result.payload, null, 2)}</pre></div>
          </div>
        )}
        {!blocked && result && "payload" in result && claimDates(result.payload).length > 0 && (
          <div className="p-4 rounded-lg border bg-card space-y-1 text-sm">
            {claimDates(result.payload).map(c => {
              const st = claimStatus(c.k, c.date.getTime() / 1000, now);
              const bad = st === "expired" || st === "not-yet-valid";
              const text = st === "expired" ? "Expired" : st === "not-expired" ? "Not expired" : st === "not-yet-valid" ? "Not yet valid" : null;
              return (
                <div key={c.k}>
                  <p>
                    <span className="text-muted-foreground">{c.label} ({c.k}):</span>{" "}
                    <span className="font-mono">{c.date.toISOString()}</span> <span className="text-xs text-muted-foreground">UTC</span>
                    {text && <span className={bad ? "ml-2 text-destructive font-medium" : "ml-2 text-green-600 font-medium"}>{text}</span>}
                  </p>
                  <p className="text-xs text-muted-foreground pl-2">Local time: <span className="font-mono">{c.date.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "long" })}</span></p>
                </div>
              );
            })}
          </div>
        )}
      </div>
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
